/**
 * ScheduleImageBackfill — one-click admin utility to copy schedule item images from Airtable into
 * Supabase Storage (api/admin/backfill-schedule-images.js). Loops the batched endpoint by offset and
 * shows running progress. Idempotent — re-running skips items that already have an image.
 *
 * TEMPORARY one-off utility — remove (with its endpoint) after the backfill is complete.
 */
import { useState } from 'react'
import { confirmDialog } from '../Dialog.jsx'

export default function ScheduleImageBackfill({ dark }) {
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState(null)   // { total, withImage } from GET
  const [progress, setProgress] = useState(null)
  const [err, setErr] = useState(null)

  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.55)'
  const border   = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'

  async function token() { return window.__clerkGetToken ? await window.__clerkGetToken() : null }

  async function check() {
    setBusy(true); setErr(null); setProgress(null)
    try {
      const res = await fetch('/api/admin/backfill-schedule-images', { headers: { Authorization: 'Bearer ' + await token() } })
      const d = await res.json(); if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status))
      setStatus(d)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  async function run() {
    if (!(await confirmDialog({ title: 'Copy images from Airtable?', message: 'Download every schedule item’s image from Airtable and store it in Supabase. Safe to re-run — items that already have an image are skipped.', confirmLabel: 'Run backfill' }))) return
    setBusy(true); setErr(null)
    const totals = { processed: 0, uploaded: 0, skipped: 0, noImage: 0, failed: 0, total: 0 }
    try {
      let offset = 0, done = false, guard = 0
      while (!done && guard++ < 200) {
        // Fresh token PER batch — Clerk session tokens are short-lived (~60s); the run takes longer,
        // so a token cached once would expire mid-loop and 401.
        const t = await token()
        const res = await fetch('/api/admin/backfill-schedule-images', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t },
          body: JSON.stringify({ offset, limit: 15 }),
        })
        const d = await res.json(); if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status))
        totals.total = d.total
        totals.processed += d.processed; totals.uploaded += d.uploaded; totals.skipped += d.skipped
        totals.noImage += d.noImage; totals.failed += d.failed
        setProgress({ ...totals, at: Math.min(d.nextOffset, d.total) })
        offset = d.nextOffset; done = d.done
      }
      setStatus(null)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  async function runLinks() {
    if (!(await confirmDialog({ title: 'Copy product links from Airtable?', message: 'Pull each item’s product URL (Link / Electrical Link) from its source Airtable record onto the item. Safe to re-run — items that already have a link are skipped.', confirmLabel: 'Run link backfill' }))) return
    setBusy(true); setErr(null)
    const totals = { processed: 0, linked: 0, skipped: 0, noLink: 0, failed: 0, total: 0 }
    try {
      let offset = 0, done = false, guard = 0
      while (!done && guard++ < 300) {
        const t = await token()
        const res = await fetch('/api/admin/backfill-schedule-images', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t },
          body: JSON.stringify({ offset, limit: 15, task: 'links' }),
        })
        const d = await res.json(); if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status))
        totals.total = d.total
        totals.processed += d.processed; totals.linked += d.linked; totals.skipped += d.skipped
        totals.noLink += d.noLink; totals.failed += d.failed
        setProgress({ uploaded: totals.linked, skipped: totals.skipped, noImage: totals.noLink, failed: totals.failed, total: totals.total, at: Math.min(d.nextOffset, d.total) })
        offset = d.nextOffset; done = d.done
      }
      setStatus(null)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const btn = (primary) => ({
    padding: '8px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
    borderRadius: 6, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
    background: primary ? '#1f9d6b' : 'none', color: primary ? '#fff' : textPrim,
    border: primary ? 'none' : `0.5px solid ${border}`,
  })

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.55, color: textMut, marginBottom: 14, maxWidth: 620 }}>
        Copies each schedule item’s image from its source Airtable record into Supabase Storage and
        links it on the item (the Schedule Builder grid shows it as a thumbnail). Idempotent — re-run
        any time; items that already have an image are skipped. One-off maintenance utility.
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button style={btn(false)} disabled={busy} onClick={check}>{busy ? 'Working…' : 'Check status'}</button>
        <button style={btn(true)} disabled={busy} onClick={run}>{busy ? 'Working…' : 'Run image backfill'}</button>
        <button style={btn(false)} disabled={busy} onClick={runLinks}>{busy ? 'Working…' : 'Run link backfill'}</button>
      </div>
      {err && <div style={{ marginTop: 12, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b' }}>{err}</div>}
      {status && !progress && (
        <div style={{ marginTop: 12, fontFamily: '"DM Mono",monospace', fontSize: 12, color: textPrim }}>
          {status.total} items · {status.withImage} already have an image
        </div>
      )}
      {progress && (
        <div style={{ marginTop: 12, fontFamily: '"DM Mono",monospace', fontSize: 12, color: textPrim }}>
          {busy ? `Working… ${progress.at}/${progress.total}` : '✓ Done.'} {` uploaded ${progress.uploaded} · skipped ${progress.skipped} · no image ${progress.noImage}` + (progress.failed ? ` · failed ${progress.failed}` : '')}
        </div>
      )}
    </div>
  )
}
